import { Link } from 'react-router-dom';
import { Info, ShieldCheck, type LucideIcon } from 'lucide-react';

import { Button } from '../../ui/button';
import { Card, CardContent } from '../../ui/card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../ui/tooltip';
import { ADMIN_BADGE_SOLID_GREEN_CLASS } from './adminSolidStatusBadge';
import type { CompanySsoDetailState } from './companySsoDetail';

export function AccessBadge({ hasAccess, label }: { hasAccess: boolean; label: string }) {
    return (
        <span
            className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                hasAccess
                    ? ADMIN_BADGE_SOLID_GREEN_CLASS
                    : 'border border-neutral-200 bg-neutral-100 text-neutral-500'
            }`}
        >
            {label}
        </span>
    );
}

export function ManageSsoButton({ ssoDetailState }: { ssoDetailState: CompanySsoDetailState }) {
    if (ssoDetailState.manageSsoDisabled || !ssoDetailState.manageSsoPath) {
        return (
            <TooltipProvider>
                <Tooltip>
                    <TooltipTrigger asChild>
                        <span className="inline-flex">
                            <Button type="button" variant="outline" disabled>
                                <ShieldCheck className="size-4" />
                                Manage SSO
                            </Button>
                        </span>
                    </TooltipTrigger>
                    <TooltipContent>
                        <p>{ssoDetailState.manageSsoTooltip}</p>
                    </TooltipContent>
                </Tooltip>
            </TooltipProvider>
        );
    }

    return (
        <Button asChild variant="outline">
            <Link to={ssoDetailState.manageSsoPath}>
                <ShieldCheck className="size-4" />
                Manage SSO
            </Link>
        </Button>
    );
}

export function AddUsersViaUsersPageNote() {
    return (
        <div className="flex gap-2.5 rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-neutral-700">
            <Info className="mt-0.5 size-4 shrink-0 text-[#276db0]" aria-hidden />
            <p>
                To add users to this company, use the{' '}
                <Link
                    to="/admin?tab=users"
                    className="font-medium text-[#276db0] underline underline-offset-2 hover:no-underline"
                >
                    Users
                </Link>{' '}
                page.
            </p>
        </div>
    );
}

export function CompanyDetailTabButton({
    active,
    icon: Icon,
    label,
    onClick,
}: {
    active: boolean;
    icon: LucideIcon;
    label: string;
    onClick: () => void;
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={`inline-flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm font-semibold text-neutral-950 transition-colors ${
                active ? 'border border-neutral-400 bg-white shadow-sm' : 'hover:bg-neutral-300/45'
            }`}
        >
            <Icon className="size-4" />
            {label}
        </button>
    );
}

export function CompanyDetailEmptyState({ message }: { message: string }) {
    return (
        <Card>
            <CardContent className="p-8 text-sm text-neutral-600">{message}</CardContent>
        </Card>
    );
}
